import classNames from "classnames";
import * as io from "io-ts";
import React from "react";
import { renderToString } from "react-dom/server";
import type {
  ActiveAnalysis,
  ActiveAnalysisQuantityValue,
} from "../../../../../api/useActiveAnalysesStream";
import type { Project } from "../../../../../api/useProjects";
import type { Setup } from "../../../../../api/useSetups";
import createGlobalState from "@ui/lib/createGlobalState";
import useStyleTag from "@ui/lib/hooks/useStyleTag";
import formatNumber from "@ui/i18n/formatNumber";
import type { Options } from "@ui/Highcharts";
import { zIndexChart } from "@ui/Highcharts/BetterHighcharts";
import { getAnalysisSetup } from "diagnostics-domain/setup/getSetup";
import type { SelectedQuantities } from "../../../useSelectedQuantities";
import useSelectedQuantities, { quantityColor } from "../../../useSelectedQuantities";
import getQuantityOffset from "../../../../../api/getQuantityOffset";
import {
  useMetrologyOverrides,
  type MetrologyOverrides,
} from "../../../../../api/useMetrologyOverrides";
import { useConstantTooltipFormatter } from "./useConstantTooltipFormatter";
import { colors } from "@ui/Theme";
import { type Observable, type Quantity } from "../../../../../api/useQuantities";
import useVisibleSeries from "@ui/Highcharts/hooks/useVisibleSeries";
import { isAnalysisQuantityValid } from "../../../metrology";
import { computeQuantityGap } from "diagnostics-domain/quantity/gap";
export const useFlagSelectedQuantities = createGlobalState({
  key: "flagSelectedQuantities",
  storage: "session",
  type: io.boolean,
  defaultValue: false,
  scoped: true,
});

interface QuantityByName {
  [quantityName: string]: Quantity;
}
interface QuantityValueByQuantityId {
  [quantityId: string]: ActiveAnalysisQuantityValue;
}
interface SelectedQuantityByName {
  [quantityName: string]: SelectedQuantities[number];
}

export const inactiveObservableClassName = "inactive-observable";

function useConstantXAxisLabelFormatter(
  quantityByName: QuantityByName,
  quantityValueByQuantityId: QuantityValueByQuantityId,
  selectedQuantityByName: SelectedQuantityByName,
  flagSelectedQuantities: boolean,
  setups: Setup[],
  analysis: ActiveAnalysis,
  _metrologyOverrides: MetrologyOverrides,
  observables?: Observable[],
) {
  const classNameSet = React.useRef(new Set());
  const { appendCSS } = useStyleTag();
  const quantityByNameRef = React.useRef(quantityByName);
  const quantityValueByQuantityIdRef = React.useRef(quantityValueByQuantityId);
  const selectedQuantityByNameRef = React.useRef(selectedQuantityByName);

  quantityByNameRef.current = quantityByName;
  quantityValueByQuantityIdRef.current = quantityValueByQuantityId;
  selectedQuantityByNameRef.current = selectedQuantityByName;

  const selectorClassName = "diagnosis-details-selector";

  React.useEffect(() => {
    appendCSS(`
    .${selectorClassName} { width: 2em; transform: rotate(180deg); }
    .${selectorClassName}.disabled { visibility: hidden; }
    .${inactiveObservableClassName} { opacity: 0.5 }
    `);
  }, [appendCSS]);

  return React.useCallback(
    function (this: Highcharts.AxisLabelsFormatterContextObject) {
      if (typeof this.value !== "string") return "";

      const quantity = quantityByNameRef.current[String(this.value)];
      const observable = observables && observables.find(o => o.id === quantity?.id);
      const isDisabled = observable && !observable.enabled;

      const quantityValue = quantity
        ? (quantityValueByQuantityIdRef.current[quantity.id] ?? null)
        : null;
      const selectedQuantity = selectedQuantityByNameRef.current[String(this.value)];

      const colorClassname =
        selectedQuantity && `DiagnosisDetails-color-${selectedQuantity?.colorIndex}`;
      if (selectedQuantity && !classNameSet.current.has(colorClassname)) {
        appendCSS(`.${colorClassname} { fill: ${quantityColor(selectedQuantity.colorIndex)};}`);
        classNameSet.current.add(colorClassname);
      }

      const valid = quantity ? isAnalysisQuantityValid(analysis, quantity) : false;

      const analysisSetup = getAnalysisSetup(setups, analysis);
      const offset = quantity ? getQuantityOffset(analysisSetup, quantity.id) : 0;
      const gapExpected =
        quantityValue && valid
          ? computeQuantityGap(quantityValue.measured, quantityValue.simulated, offset)
          : null;

      return renderToString(
        <span className={isDisabled ? inactiveObservableClassName : undefined}>
          {this.value}
          <span
            className={classNames(selectorClassName, colorClassname, {
              disabled: !selectedQuantity || !flagSelectedQuantities,
            })}
          >
            {" ⬅︎"}
          </span>

          {gapExpected === null ? null : (
            <>
              <br />
              {formatNumber(gapExpected, {
                precision: 2,
                unit: quantity ? (quantity.unit ?? undefined) : undefined,
                sign: true,
              })}
            </>
          )}
        </span>,
      );
    },
    [analysis, setups, flagSelectedQuantities, appendCSS, observables],
  );
}

function useXLabelClickCallback(
  enabled: boolean,
  quantityByName: QuantityByName,
  selectedQuantityByName: SelectedQuantityByName,
  setSelectedQuantities: (quantities: Quantity[]) => void,
) {
  const enabledRef = React.useRef(enabled);
  enabledRef.current = enabled;
  const selectedQuantityByNameRef = React.useRef<SelectedQuantityByName>(selectedQuantityByName);
  selectedQuantityByNameRef.current = selectedQuantityByName;
  const quantityByNameRef = React.useRef<QuantityByName>(quantityByName);
  quantityByNameRef.current = quantityByName;

  return React.useCallback(
    (event: MouseEvent, label: { value: string }) => {
      if (!enabledRef.current) return;

      const quantities = Object.values(selectedQuantityByNameRef.current).map(s => s.quantity);
      const quantity = quantityByNameRef.current[label.value];
      if (!quantity) throw new Error("Cannot happen");

      if (event.metaKey || event.shiftKey || event.ctrlKey) {
        if (selectedQuantityByNameRef.current[label.value]) {
          setSelectedQuantities(quantities.filter(q => q.name !== quantity.name));
        } else {
          setSelectedQuantities([...quantities, quantity]);
        }
      } else {
        setSelectedQuantities([quantity]);
      }
    },
    [setSelectedQuantities],
  );
}

export function useCommonOptions({
  visibleSymptomHistogramChartId,
  series,
  analysis,
  quantities,
  project,
  setups,
  flagSelectedQuantities,
  observables,
}: {
  visibleSymptomHistogramChartId: string;
  series: Highcharts.SeriesColumnOptions[];
  analysis: ActiveAnalysis;
  project: Project;
  setups: Setup[];
  quantities: Quantity[];
  flagSelectedQuantities: boolean;
  observables?: Observable[];
}): Options {
  const metrologyOverrides = useMetrologyOverrides(project.id);
  const [selectedQuantities, setSelectedQuantities] = useSelectedQuantities(project.id);
  const [visibleSeries] = useVisibleSeries(visibleSymptomHistogramChartId, series);
  const selectedQuantityByName = React.useMemo(
    () =>
      selectedQuantities.reduce<SelectedQuantityByName>((acc, selectedQuantity) => {
        acc[selectedQuantity.quantity.name] = selectedQuantity;
        return acc;
      }, {}),
    [selectedQuantities],
  );
  const quantityByName = React.useMemo(
    () =>
      quantities.reduce<QuantityByName>((acc, quantity) => {
        acc[quantity.name] = quantity;
        return acc;
      }, {}),
    [quantities],
  );
  const quantityValueByQuantityId = React.useMemo(
    () =>
      Object.keys(analysis.quantityValues).reduce<QuantityValueByQuantityId>((acc, quantityId) => {
        const quantityValues = analysis.quantityValues[quantityId];
        if (quantityValues) {
          acc[quantityId] = quantityValues;
        }

        return acc;
      }, {}),
    [analysis],
  );

  const constantXAxisLabelFormatter = useConstantXAxisLabelFormatter(
    quantityByName,
    quantityValueByQuantityId,
    selectedQuantityByName,
    flagSelectedQuantities,
    setups,
    analysis,
    metrologyOverrides,
    observables,
  );
  const constantTooltipFormatter = useConstantTooltipFormatter(quantityByName, true);
  const onXLabelClick = useXLabelClickCallback(
    flagSelectedQuantities,
    quantityByName,
    selectedQuantityByName,
    setSelectedQuantities,
  );

  return React.useMemo(() => {
    const options: Options = {
      initial: {},
      series: visibleSeries,
      deferSeriesRender: false,
      chart: {
        fitWrapperHeight: false,
        type: "column",
        height: 480,
        ignoreHiddenSeries: true,
      },
      plotOptions: {
        series: {
          allowPointSelect: false,
        },
        column: {
          borderWidth: 2,
          stacking: "normal",
          states: {
            hover: {
              brightness: 0,
            },
          },
          maxPointWidth: 50,
          pointPadding: 0.1,
          groupPadding: 0.3,
        },
      },
      tooltip: {
        formatter: constantTooltipFormatter,
        shared: true,
        borderColor: colors.titanium100,
        useHTML: true,
        followPointer: true,
      },
      xAxis: {
        type: "category",
        labels: {
          rotation: -80,
          align: "left",
          x: -15,
          y: -15,
          formatter: constantXAxisLabelFormatter,
          style: {
            cursor: flagSelectedQuantities ? "pointer" : undefined,
            color: colors.carbon800,
          },
          onClick: onXLabelClick,
        },
        lineWidth: 0,
        opposite: true,
        crosshair: true,
        disableAnimation: true,
      },
      yAxis: {
        allowDecimals: false,
        labels: {
          sign: true,
        },
        grid: {
          enabled: true,
        },
        lineWidth: 1,
        gridLineWidth: 1,
        maxRange: 1,
        plotLines: [
          {
            value: 0,
            width: 1,
            zIndex: zIndexChart.plotLines,
            color: colors.titanium100,
          },
        ],
        minPadding: 0.1,
        maxPadding: 0.1,
      },
    };

    return options;
  }, [
    visibleSeries,
    constantXAxisLabelFormatter,
    onXLabelClick,
    flagSelectedQuantities,
    constantTooltipFormatter,
  ]);
}
